/**
 * Financial Filters
 *
 * Filters related to financial metrics: yield, ROI, cashflow, pricing
 * To add a new financial filter, simply add a new object to this array
 */

import { Filter } from '../../types/filters';

export const financialFilters: Filter[] = [
  // ===== GROSS YIELD =====
  {
    id: 'gross_yield',
    name: 'Gross Yield',
    description: 'Annual rental income as percentage of purchase price',
    category: 'financial',
    type: 'range',
    minTier: 'STARTER',
    premiumFeature: false,
    popularityRank: 1,
    dataPath: 'teaser.estimatedYield', // Backend returns this as "5.2%" string
    version: '1.0',
    enabled: true,
    defaultValue: [6.5, 30],
    config: {
      min: 0,
      max: 100,
      step: 0.5,
      defaultMin: 6.5,
      defaultMax: 30,
      unit: '%',
      format: (value: number) => `${value.toFixed(1)}%`,
      presets: [
        { label: 'Reset to Default', value: [6.5, 30], icon: '' },
        { label: 'Good (6.5%+)', value: [6.5, 100], icon: '' },
        { label: 'Excellent (8%+)', value: [8, 100], icon: '' },
        { label: 'Outstanding (10%+)', value: [10, 100], icon: '' }
      ]
    }
  },

  // ===== ESTIMATED MONTHLY RENT =====
  {
    id: 'estimated_rent',
    name: 'Estimated Monthly Rent',
    description: 'Expected monthly rental income',
    category: 'financial',
    type: 'range',
    minTier: 'STARTER',
    premiumFeature: false,
    popularityRank: 2,
    dataPath: 'teaser.estimatedRent', // Available in teaser
    version: '1.0',
    enabled: true,
    defaultValue: [0, 5000],
    config: {
      min: 0,
      max: 5000,
      step: 100,
      defaultMin: 0,
      defaultMax: 5000,
      unit: '£',
      format: (value: number) => `£${value.toLocaleString()}`,
      presets: [
        { label: '£500+/month', value: [500, 5000], icon: '' },
        { label: '£1000+/month', value: [1000, 5000], icon: '' },
        { label: '£1500+/month', value: [1500, 5000], icon: '' }
      ]
    }
  },

  // ===== PURCHASE PRICE =====
  {
    id: 'purchase_price',
    name: 'Purchase Price',
    description: 'Property asking price',
    category: 'financial',
    type: 'range',
    minTier: 'STARTER',
    premiumFeature: false,
    popularityRank: 3,
    dataPath: 'source.askingPrice', // Available in source
    version: '1.0',
    enabled: true,
    defaultValue: [0, 1000000],
    config: {
      min: 0,
      max: 50000000,
      step: 10000,
      defaultMin: 0,
      defaultMax: 1000000,
      unit: '£',
      format: (value: number) => {
        if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `£${(value / 1000).toFixed(0)}k`;
        return `£${value}`;
      },
      presets: [
        { label: 'Under £100k', value: [0, 100000], icon: '' },
        { label: '£100k-£500k', value: [100000, 500000], icon: '' },
        { label: '£500k-£1M', value: [500000, 1000000], icon: '' },
        { label: '£1M+', value: [1000000, 50000000], icon: '' }
      ]
    }
  }
];
